import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import axios from "axios";
import { getIsAdmin, getIsCountryAdmin } from "helpers/users";

import { setIsInitializationRequired } from "data-handler/ducks/initialization";
import { errorToast } from "data-handler/ducks/toast";

// TODO: these consts DON'T need to be exposed, other modules must use action creators!
export const REQUEST = "schoolconnect/auth/REQUEST_LOGIN";
export const SUCCESS = "schoolconnect/auth/SUCCESS_LOGIN";
export const FAILURE = "schoolconnect/auth/FAILURE_LOGIN";
export const LOGIN_LOCAL = "LOGIN_LOCAL";
export const LOGOUT_LOCAL = "LOGOUT_LOCAL";
export const LOGOUT = "LOGOUT";
export const LOGOUT_SUCCESS = "LOGOUT_SUCCESS";
export const LOGOUT_FAILURE = "LOGOUT_FAILURE";
export const LOGIN_CIAM = "LOGIN_CIAM";
export const REFRESH_LAST_ACTION_PERFORMED_AT =
  "REFRESH_LAST_ACTION_PERFORMED_AT";
export const REQUIRE_ONLINE_LOGIN = "REQUIRE_ONLINE_LOGIN";
export const VIEW_LAYOUT = "VIEW_LAYOUT";
export const SET_INITIALIZATION = "schoolconnect/auth/SET_INITIALIZATION";

const OFFLINE_LOGIN = "OFFLINE_LOGIN";
const ONLINE_LOGIN = "ONLINE_LOGIN";
const initialState = {
  currentUser: undefined,
  error: null,
  errorResponse: null,
  fetching: false,
  token: undefined,
  refreshToken: undefined,
  lastActionPerformedAt: undefined,
  is_active: undefined,
  isCIAM: false,
  isCiamAuthenticated: false,
  onlineRequiredError: false,
  viewLayout: "auto",
};

function reducer(state = initialState, action) {
  switch (action.type) {
    case REQUEST:
      return { ...state, fetching: true, error: null };
    case SUCCESS:
      return {
        ...state,
        currentUser: action.data.user,
        fetching: false,
        loginMode: ONLINE_LOGIN,
        token: action.data.token,
        onlineRequiredError: false,
      };
    case FAILURE:
      return {
        ...state,
        currentUser: undefined,
        error: action.error,
        errorResponse: action.error.response,
        fetching: false,
        token: undefined,
        refreshToken: undefined,
        lastActionPerformedAt: undefined,
        onlineRequiredError: false,
      };
    case LOGIN_LOCAL:
      return {
        ...state,
        currentUser: action.data,
        loginMode: OFFLINE_LOGIN,
        onlineRequiredError: false,
        token: "123", //TODO: set var, action.data.localData.token,
        refreshToken: undefined,
        lastActionPerformedAt: undefined,
      };
    case LOGOUT_LOCAL:
      let newAuth = state;
      delete newAuth.user;
      return newAuth;
    case LOGOUT:
      return {
        ...state,
        error: null,
        errorResponse: null,
      };
    case LOGOUT_SUCCESS:
      return initialState;
    case LOGOUT_FAILURE:
      return {
        ...state,
        error: action.error,
        errorResponse: action.error.response,
      };
    case LOGIN_CIAM:
      // the httpOnly session cookie is the credential now. Never hold CIAM
      // tokens in redux - redux-persist would write them straight to
      // localStorage, which is the exposure this change exists to remove.
      const currentUser =
        action.data && action.data.user ? { ...action.data.user } : undefined;
      if (currentUser) {
        delete currentUser.token;
        delete currentUser.refresh_token;
      }
      // TODO: move role into backend in a nicer way
      let role;
      if (action.data.user && action.data.user.school) {
        const school = action.data.user.school;
        role = school.is_principal
          ? "principal_at" //TODO: better role naming and handling in backend
          : school.is_teacher
          ? "teacher_at"
          : school.is_store_keeper
          ? "store_keeper_at"
          : undefined;
      }
      const is_active = action.data.is_active;
      return {
        ...state,
        currentUser: { ...currentUser, role, isCIAM: true },
        fetching: false,
        is_active: is_active,
        isCIAM: true,
        loginMode: ONLINE_LOGIN,
        onlineRequiredError: false,
        isCiamAuthenticated: true,
        token: undefined,
        refreshToken: undefined,
        lastActionPerformedAt: undefined,
      };
    case REFRESH_LAST_ACTION_PERFORMED_AT:
      return {
        ...state,
        lastActionPerformedAt: action.data,
      };
    // Must not touch lastActionPerformedAt: the 401 interceptor branches on
    // it, and clearing it here used to permanently disable 401 handling for
    // the rest of the session
    case REQUIRE_ONLINE_LOGIN:
      return {
        ...state,
        onlineRequiredError: action.data?.requireOnlineLogin,
      };
    case VIEW_LAYOUT:
      return {
        ...state,
        viewLayout: action.data,
      };

    default:
      return state;
  }
}

const persistedReducer = persistReducer(
  {
    key: "auth",
    storage,
    blacklist: ["error", "errorResponse", "fetching"],
  },
  reducer
);

export default persistedReducer;

//
// ACTION CREATORS
//

export const requestLogin = (data) => ({ type: REQUEST, data });

export const setViewLayout = (data) => ({ type: VIEW_LAYOUT, data });

export const setOnlineRequiredError = () => ({
  type: REQUIRE_ONLINE_LOGIN,
  data: { requireOnlineLogin: true },
});
export const unsetOnlineRequiredError = () => ({
  type: REQUIRE_ONLINE_LOGIN,
  data: { requireOnlineLogin: false },
});

//
// SELECTORS
//

export const getAuthErrorResponse = (state) => state.auth.errorResponse;
export const getToken = (state) => state.auth?.token;
export const getIsCiamAuthenticated = (state) =>
  state.auth?.isCiamAuthenticated === true;
export const getRefreshToken = (state) => state.auth?.refreshToken;
export const getLastActionPerformedAt = (state) =>
  state.auth?.lastActionPerformedAt;
export const getCurrentUser = (state) => state.auth.currentUser;
export const getIsActive = (state) => state.auth.is_active;
export const getIsWFPUser = (state) => state.auth.currentUser?.is_wfp;
export const getIsSuperuser = (state) =>
  state.auth.currentUser?.is_superuser === true;

export const getLoggedInOffline = (state) =>
  state.auth.loginMode === OFFLINE_LOGIN;
export const getOnlineRequiredError = (state) => state.auth.onlineRequiredError;
export const getLoginState = (state) => ({
  fetching: state.auth.fetching,
  error: state.auth.error,
  errorResponse: state.auth.errorResponse,
});
export const getUserPermissionGroups = (state) =>
  state.auth.currentUser?.groups;
export const getCurrentUserCountryId = (state) => {
  return state.auth.currentUser?.country?.id;
};

export const getRawDataExtractionPermission = (state) => {
  return state.auth.currentUser?.has_raw_data_extraction_permission;
};

export const getReportApproverAccess = (state) => {
  return state.auth.currentUser?.has_report_approver_access;
};

export const getIsViewer = (state) =>
  getCurrentUser(state)?.is_external_viewer ||
  getCurrentUser(state)?.is_wfp_viewer;

export const getIsApprover = (state) => state.auth.currentUser?.is_approver;

export const getIsSchoolStaff = (state) =>
  state.auth.currentUser?.school?.is_teacher ||
  state.auth.currentUser?.role === "teacher" ||
  state.auth.currentUser?.role?.type === "teacher" ||
  state.auth.currentUser?.role_type === "teacher" ||
  state.auth.currentUser?.school?.is_store_keeper ||
  state.auth.currentUser?.role === "store_keeper" ||
  state.auth.currentUser?.role?.type === "store_keeper" ||
  state.auth.currentUser?.role_type === "store_keeper" ||
  state.auth.currentUser?.school?.is_principal ||
  state.auth.currentUser?.role === "principal" ||
  state.auth.currentUser?.role?.type === "principal" ||
  state.auth.currentUser?.role_type === "principal";

export const getIsSchoolAdmin = (state) =>
  state.auth.currentUser?.school?.is_principal;

export const getRoleList = (state) => [
  { is_viewer: getIsViewer(state) },
  { is_admin: getIsAdmin(state.auth.currentUser) },
  { is_countryAdmin: getIsCountryAdmin(state.auth.currentUser) },
  { is_wfp: getIsWFPUser(state) },
  { is_approver: getIsApprover(state) },
  { is_school_staff: getIsSchoolStaff(state) },
  { is_school_admin: getIsSchoolAdmin(state) },
];

export const getIsOfflineMode = (state) =>
  state.auth.loginMode === OFFLINE_LOGIN;

export const getIsOnlineMode = (state) => state.auth.loginMode === ONLINE_LOGIN;

export const getViewLayout = (state) => state.auth.viewLayout;

export const getIsCiamLogin = (state) => state.auth.isCIAM;

//
// SAGA
//

export function* loginSagaWatcher() {
  yield takeEvery(REQUEST, loginSagaWorker);
}

function POSTLogin({ data }) {
  const url = `${process.env.REACT_APP_API_URL}/auth/login/`;
  return axios({
    method: "POST",
    url: url,
    data: data,
  });
}

function* loginSagaWorker(data) {
  let response;
  try {
    response = yield call(POSTLogin, data);
    yield put({ type: SUCCESS, data: response.data });
    yield put(setIsInitializationRequired(true));
  } catch (error) {
    yield put({ type: FAILURE, error });
    yield put(setIsInitializationRequired(false));
  }
}

export function* logoutSagaWatcher() {
  yield takeLatest(LOGOUT, logoutSagaWorker);
}

function POSTLogout() {
  const url = `${process.env.REACT_APP_API_URL}/logout/`;
  return axios({
    method: "POST",
    url: url,
    data: {},
  });
}

export function* logoutSagaWorker() {
  try {
    const isCIAM = yield select(getIsCiamLogin);
    let logoutUrl;
    let reachedServer = true;

    if (isCIAM) {
      try {
        // the backend holds the id_token now, so it builds the CIAM logout URL
        const response = yield call(POSTLogout);
        logoutUrl = response?.data?.logout_url;
      } catch (error) {
        // a device with no connection still has to be able to sign out; the
        // server session expires on its own
        reachedServer = false;
      }
    }

    yield put({ type: LOGOUT_SUCCESS });

    if (isCIAM && reachedServer) {
      window.open(
        logoutUrl ||
          `${process.env.REACT_APP_CIAM_LOGOUT_URL}?post_logout_redirect_uri=${process.env.REACT_APP_CIAM_REDIRECT_URL}`,
        "_self"
      );
    } else {
      window.location.replace("/login");
    }
  } catch (error) {
    yield put({ type: LOGOUT_FAILURE, error });
    yield put(errorToast());
  }
}
